import { useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  alpha,
  Button,
  Divider,
  Link,
  ListItemText,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tabs,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';
import { hasPermission } from 'src/utils/has-permissions';
import { TABLE_HEAD_CELL_TYPE, TRANSACTION_TYPE } from 'src/utils/common-types';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { getBarTransactionsById } from 'src/redux/slices/bar';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import { BarAvailabilityStatus, IBarHistory, IBarItem } from 'src/types/bar';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'transactionNo', label: 'Transaction #', align: 'left', primary: true },
  {
    id: 'purchaseOrDispatchDate',
    label: 'Transaction Date',
    align: 'left',
    primary: true,
  },
  { id: 'type', label: 'Transaction Type', align: 'left', primary: true },
  // {
  //   id: 'createdBy',
  //   label: 'Created By',
  //   align: 'left',
  //   primary: true,
  // },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },

  { id: 'updatedAt', label: 'Updated At', primary: true, align: 'left' },
];

const SUPPLIERS_LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'supplierNo', label: '#', align: 'left', primary: true },
  { id: 'supplierName', label: 'Company Name', align: 'left', primary: true },
  {
    id: 'contactPerson',
    label: 'Contact Person',
    align: 'left',
    primary: true,
  },
  {
    id: 'phoneNumber',
    label: 'Phone Number',
    align: 'left',
    primary: true,
  },
];

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'transactions', label: 'Transactions History' },
];

const defaultSortBy: { sortBy: string; order: 'asc' | 'desc' } = {
  sortBy: 'updatedAt',
  order: 'desc',
};

type Props = {
  currentBarItem?: IBarItem;
};

export default function BarItemDetails({ currentBarItem }: Props) {
  const [tabValue, setTabValue] = useState('overview');

  const { user: authUser } = useAuthContext();

  const dispatch = useAppDispatcher();

  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const { page, rowsPerPage } = table;

  const [sort, setSort] = useState(defaultSortBy);

  const {
    transactions: { status, transactions, error },
  } = useAppSelector((state) => state.bar);

  useEffect(() => {
    if (tabValue === 'transactions') {
      dispatch(
        getBarTransactionsById({
          itemID: currentBarItem?._id ?? '',
          page,
          limit: rowsPerPage,
          sortBy: sort.sortBy,
          sort: sort.order,
        })
      );
    }
  }, [tabValue, currentBarItem, dispatch, page, rowsPerPage, sort]);

  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(_, value) => setTabValue(value)}
            sx={{
              px: 2.5,
              boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
            }}
          >
            {TABS.map((tab) => (
              <Tab key={tab.value} iconPosition="end" value={tab.value} label={tab.label} />
            ))}
          </Tabs>

          {tabValue === 'overview' && (
            <Grid container spacing={2} mt={2}>
              <Grid
                mb={1}
                display="flex"
                mt={2}
                alignItems="center"
                justifyContent="space-between"
                width={1}
              >
                <Typography variant="subtitle1">Basic Information</Typography>
                {hasPermission(
                  authUser,
                  'any',
                  AuthPermissions.UPDATE_BAR_ITEM,
                  AuthPermissions.ADMIN_PERMISSION,
                  AuthPermissions.SUPER_ADMIN_PERMISSION
                ) && (
                  <Link
                    component={RouterLink}
                    href={paths.dashboard.bar.edit(currentBarItem?._id ?? '')}
                  >
                    <Button variant="text" startIcon={<Iconify icon="solar:pen-bold" />}>
                      Edit
                    </Button>
                  </Link>
                )}
              </Grid>

              <Grid xs={12} md={2}>
                <ListItemText
                  primary={currentBarItem?.itemName}
                  secondary={currentBarItem?.itemCode}
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={2}>
                <ListItemText
                  primary={fCurrencyRupees(Number(currentBarItem?.price ?? 0) * 100)}
                  secondary="Price"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={2}>
                <ListItemText
                  primary={`${currentBarItem?.reorderThreshold} ${currentBarItem?.unit.abbreviation}`}
                  secondary="Re-order threshold"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={2}>
                <ListItemText
                  primary={`${currentBarItem?.group}`}
                  secondary="Group"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={2}>
                <ListItemText
                  primary={`${currentBarItem?.volume}l`}
                  secondary="Volume (l)"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Divider
                sx={{
                  my: 2.5,
                  typography: 'overline',
                  color: 'text.disabled',
                  '&:before, :after': {
                    borderTopStyle: 'dashed',
                  },
                }}
              />

              <Grid mb={1} xs={12} width="full">
                <Typography mt={2} variant="subtitle1">
                  Operational Information
                </Typography>
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={`${currentBarItem?.quantity} ${currentBarItem?.unit.abbreviation}`}
                  secondary="Running Quantity"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={
                    <Label
                      variant="soft"
                      color={
                        (currentBarItem?.availability === BarAvailabilityStatus.IN_STOCK &&
                          'success') ||
                        (currentBarItem?.availability === BarAvailabilityStatus.OUT_OF_STOCK &&
                          'error') ||
                        (currentBarItem?.availability === BarAvailabilityStatus.RUNNING_LOW &&
                          'warning') ||
                        'default'
                      }
                    >
                      {currentBarItem?.availability.split('-').join(' ')}
                    </Label>
                  }
                  secondary="Status"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={
                    <Label
                      variant="soft"
                      color={currentBarItem?.alertEnabled ? 'success' : 'warning'}
                    >
                      {currentBarItem?.alertEnabled ? 'Enabled' : 'Disabled'}
                    </Label>
                  }
                  secondary="Alerts"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              <Grid xs={12} md={3}>
                <ListItemText
                  primary={
                    <Link
                      component={RouterLink}
                      href={paths.dashboard.user.view(currentBarItem?.updatedBy._id ?? '')}
                    >
                      {`${currentBarItem?.updatedBy.firstName} ${currentBarItem?.updatedBy.lastName}`}
                    </Link>
                  }
                  secondary="Last Updated"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{
                    component: 'span',
                    color: 'text.disabled',
                  }}
                />
              </Grid>

              {currentBarItem?.suppliers && currentBarItem?.suppliers.length > 0 && (
                <>
                  <Grid mb={1} xs={12} width="full">
                    <Typography mt={2} variant="subtitle1">
                      Suppliers Information
                    </Typography>
                  </Grid>

                  <Grid xs={12}>
                    <TableContainer
                      sx={{ position: 'relative', overflow: 'hidden', height: '200px', mt: 2 }}
                    >
                      <Scrollbar>
                        <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                          <TableHeadCustom headLabel={SUPPLIERS_LINE_ITEM_HEADERS} />

                          <TableBody>
                            {currentBarItem?.suppliers.map((supplier, index) => (
                              <TableRow hover key={supplier._id}>
                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {index + 1 < 10 ? `0${index + 1}` : index}
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  <Link
                                    component={RouterLink}
                                    href={paths.dashboard.suppliers.view(supplier._id)}
                                  >
                                    {supplier?.supplierName}
                                  </Link>
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {supplier?.contactPerson}
                                </TableCell>

                                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                  {supplier?.phoneNumber}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Scrollbar>
                    </TableContainer>
                  </Grid>
                </>
              )}
            </Grid>
          )}

          {tabValue === 'transactions' && (
            <Stack>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                {currentBarItem?.itemName}
              </Typography>
              <TableContainer
                sx={{ position: 'relative', overflow: 'hidden', height: '400px', mt: 2 }}
              >
                <Scrollbar>
                  <Table stickyHeader size="medium" sx={{ minWidth: 960 }}>
                    <TableHeadCustom
                      headLabel={LINE_ITEM_HEADERS}
                      order={sort.order}
                      orderBy={sort.sortBy}
                      rowCount={transactions.items.length}
                      onSort={(id) => {
                        if (id === sort.sortBy) {
                          setSort((prev) => ({
                            ...prev,
                            order: prev.order === 'asc' ? 'desc' : 'asc',
                          }));
                        } else {
                          setSort({
                            sortBy: id,
                            order: 'asc',
                          });
                        }
                      }}
                    />

                    <TableBody>
                      {status === 'loading' ? (
                        [...Array(10)].map((i, index) => <TableSkeleton key={index} />)
                      ) : (
                        <>
                          {transactions?.items.map((row: IBarHistory, index: number) => (
                            <TableRow hover key={row.transactionDetails._id}>
                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                <Link
                                  component={RouterLink}
                                  href={
                                    row.transactionType === TRANSACTION_TYPE.PURCHASE
                                      ? paths.dashboard.bar.purchase.view(
                                          row.transactionDetails._id
                                        )
                                      : paths.dashboard.bar.dispatch.view(
                                          row.transactionDetails._id
                                        )
                                  }
                                >
                                  {row.transactionDetails.transactionCode}
                                </Link>
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.transactionDetails.purchaseOrDispatchDate)}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                <Label
                                  variant="soft"
                                  color={
                                    (row.transactionType === TRANSACTION_TYPE.DISPATCH &&
                                      'success') ||
                                    (row.transactionType === TRANSACTION_TYPE.PURCHASE &&
                                      'error') ||
                                    (row.transactionType === TRANSACTION_TYPE.DISPOSAL &&
                                      'warning') ||
                                    'default'
                                  }
                                >
                                  {row.transactionType.split('-').join(' ')}
                                </Label>
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {row.quantity} {row.unit.abbreviation}
                              </TableCell>

                              <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                                {fDateTime(row.updatedAt)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </>
                      )}

                      {status !== 'failure' && transactions.count === 0 && (
                        <TableNoData
                          message="No transactions found"
                          notFound={transactions.count === 0}
                        />
                      )}

                      {status === 'failure' && <TableErrorData notFound error={error} />}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
              <TablePaginationCustom
                count={transactions.count}
                page={table.page}
                rowsPerPage={table.rowsPerPage}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
                dense={table.dense}
                onChangeDense={table.onChangeDense}
              />
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" gap={2}>
            <Link component={RouterLink} href={paths.dashboard.bar.root()}>
              <Button variant="outlined" color="inherit">
                Cancel
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
